
import React, { forwardRef, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';

interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  query: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  autoFocus?: boolean;
  placeholder?: string;
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(({ 
  query, 
  onChange, 
  onClear, 
  autoFocus = false,
  placeholder = "Search for stories, videos, tags...",
  ...props 
}, ref) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Only apply autofocus on non-mobile devices to prevent keyboard popup
  useEffect(() => {
    if (autoFocus && !isMobile && inputRef.current) {
      // Short delay to ensure the component is fully mounted
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [autoFocus, isMobile]);
  
  return (
    <div className={cn(
      "flex-1 relative",
      "rounded-md",
      isDark ? "bg-zinc-800" : "bg-zinc-100"
    )}>
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400">
        <Search size={18} />
      </div>
      
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={onChange}
        placeholder={placeholder}
        className={cn(
          "w-full bg-transparent border-0 py-2 pl-10 pr-10",
          "focus:ring-0 focus:outline-none rounded-md",
          "touch-manipulation",
          isDark ? 
            "text-white placeholder:text-zinc-500" : 
            "text-zinc-900 placeholder:text-zinc-500"
        )}
        {...props}
      />
      
      {query && (
        <button
          type="button"
          onClick={onClear}
          className={cn(
            "absolute right-3 top-1/2 transform -translate-y-1/2",
            "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200",
            "touch-manipulation"
          )}
          aria-label="Clear search"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;
